
import React from 'react';
import { CardHeader, CardTitle } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ImageUploader } from './ImageUploader';
import { Skeleton } from '@/components/ui/skeleton';

interface ProfileHeaderProps {
  coverImage?: string;
  profileImage: string;
  breederProfileImage: string;
  onImageUpdate: (type: 'profile' | 'cover', url: string) => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  coverImage,
  profileImage,
  breederProfileImage,
  onImageUpdate
}) => {
  const [imageLoading, setImageLoading] = React.useState({
    profile: false,
    cover: false
  });

  const handleImageLoad = (type: 'profile' | 'cover') => {
    setImageLoading(prev => ({ ...prev, [type]: false }));
  };

  const handleImageError = (type: 'profile' | 'cover') => {
    setImageLoading(prev => ({ ...prev, [type]: false }));
  };

  React.useEffect(() => {
    if (profileImage) {
      setImageLoading(prev => ({ ...prev, profile: true }));
    }
  }, [profileImage]);

  React.useEffect(() => {
    if (coverImage) {
      setImageLoading(prev => ({ ...prev, cover: true }));
    }
  }, [coverImage]);

  // Get first letter of name for profile image fallback
  const getInitial = (name: string) => {
    return name ? name.charAt(0).toUpperCase() : 'B';
  };

  // Extract name from breederProfileImage or use fallback
  const extractNameFromProfile = () => {
    // This is a simplified approach - in a real app you'd have access to the actual name
    // For now, we'll use 'B' as default or try to extract from some other source
    return 'B'; // You might want to pass the actual name as a prop
  };

  return (
    <CardHeader className="p-0 relative z-20">
      {/* Cover Image */}
      <AspectRatio ratio={4 / 1} className="relative">
        {coverImage ? (
          <>
            {imageLoading.cover && (
              <Skeleton className="w-full h-full absolute inset-0" />
            )}
            <img 
              src={coverImage} 
              alt="Cover"
              className={`w-full h-full object-cover transition-opacity duration-500 ${
                imageLoading.cover ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => handleImageLoad('cover')}
              onError={() => handleImageError('cover')}
            />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600" />
        )}
        <ImageUploader
          type="cover"
          currentImage={coverImage}
          onImageUpdate={onImageUpdate}
          className="absolute top-4 right-4 rounded-full bg-white/90 hover:bg-white z-10"
          iconClassName="w-4 h-4"
        />
        
        {/* Profile Image - positioned to overlap cover image boundary */}
        <div className="absolute -bottom-12 left-6 z-20">
          <div className="relative">
            <div className="w-20 h-20 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg overflow-hidden">
              {profileImage ? (
                <>
                  {imageLoading.profile && (
                    <Skeleton className="w-full h-full rounded-full" />
                  )}
                  <img 
                    src={profileImage} 
                    alt="Profile"
                    className={`w-full h-full object-cover transition-opacity duration-500 ${
                      imageLoading.profile ? 'opacity-0' : 'opacity-100'
                    }`}
                    onLoad={() => handleImageLoad('profile')}
                    onError={() => handleImageError('profile')}
                  />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center">
                  <span className="text-white text-4xl font-bold">
                    {getInitial(extractNameFromProfile())}
                  </span>
                </div>
              )}
            </div>
            <ImageUploader
              type="profile"
              currentImage={profileImage}
              onImageUpdate={onImageUpdate}
              className="absolute bottom-1 right-1 rounded-full w-8 h-8 bg-white hover:bg-gray-50 z-10"
              iconClassName="w-3 h-3"
            />
          </div>
        </div>
      </AspectRatio>

      {/* Aspect ratio recommendation */}
      <div className="px-6 pt-2 pb-4 flex justify-end">
        <p className="text-sm text-muted-foreground w-1/2 md:w-auto text-right">
          Recommended aspect ratio for cover image: 4:1
        </p>
      </div>

      <CardTitle className="pt-16 px-6 pb-2 relative">Profile Information</CardTitle>
    </CardHeader>
  );
};
